import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSync,
  faFilter,
  faEdit,
  faSave,
  faTimes,
  faCalendarAlt,
  faShip,
  faBoxOpen,
  faFileAlt,
  faWarehouse,
  faTruck,
  faTable,
  faDownload,
  faBell,
  faUser,
  faSignOutAlt,
  faQuestionCircle,
  faCog,
  faEnvelope,
  faChevronDown,
  faSort,
  faSortUp,
  faSortDown,
  faEye,
  faCheckCircle,
  faExclamationTriangle,
  faHistory,
  faPlus,
  faTrash,
  faClock,
  faBolt, // Add this
} from "@fortawesome/free-solid-svg-icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import EditableCell from "./EditableCell";
import DataCell from "./DataCell";
import StatusBadge from "./StatusBadge";
import StatusHistory from "./StatusHistory";
import KeyboardShortcutsHelper from "./KeyboardShortcutsHelper";
import ToastContainer from "./ToastContainer";
import KpiCell from "./KpiCell";
import {
  calculatePrealertaKpi,
  calculateAltaPisoKpi,
  calculateAltaAtracoKpi,
  calculateAltaGlosaKpi,
  KPI_RULES,
} from "../utils/kpiUtils";
import websocketService from "../services/websocket";
import RealTimeDashboard from "./RealTimeDashboard";
import { motion, AnimatePresence } from "framer-motion";

// Status color mapping
const statusColors = {
  Alert: "bg-yellow-100 text-yellow-800",
  "In Progress": "bg-blue-100 text-blue-800",
  Arribo: "bg-green-100 text-green-800",
  "Desconsolidacion LCL": "bg-purple-100 text-purple-800",
  Revalidacion: "bg-teal-100 text-teal-800",
  default: "bg-gray-100 text-gray-800",
};

// Format date function
const formatDate = (date) => {
  if (!date) return "-";
  const d = new Date(date);
  return d.toLocaleString("es-MX", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Main component
const BookingControlDeck = () => {
  // State variables
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: "updatedAt",
    direction: "desc",
  });
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    aduana: "",
    dateRange: {
      start: null,
      end: null,
    },
  });
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize] = useState(100); // You can make this configurable if needed

  // Add real-time state
  const [showRealTimeDashboard, setShowRealTimeDashboard] = useState(false);
  const [realtimeChanges, setRealtimeChanges] = useState(new Map());
  const [realtimeAlerts, setRealtimeAlerts] = useState([]);
  const [activeViewers, setActiveViewers] = useState(new Map());
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [activityMetrics, setActivityMetrics] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);

  // Generate query params for Excel export
  const getExportQueryParams = () => {
    const params = new URLSearchParams();

    if (searchTerm) params.append("search", searchTerm);
    if (filters.status) params.append("status", filters.status);
    if (filters.aduana) params.append("aduana", filters.aduana);
    if (filters.dateRange.start)
      params.append("dateStart", filters.dateRange.start.toISOString());
    if (filters.dateRange.end)
      params.append("dateEnd", filters.dateRange.end.toISOString());

    return params.toString() ? `?${params.toString()}` : "";
  };

  // Sample notifications
  const sampleNotifications = [
    {
      id: 1,
      text: "Booking ECL-10513/04042572 status updated to Revalidacion",
      time: "10 minutes ago",
      read: false,
    },
    {
      id: 2,
      text: "New booking created: EOP-10614/0404259",
      time: "2 hours ago",
      read: false,
    },
    {
      id: 3,
      text: "ETA date updated for 5 bookings",
      time: "Yesterday",
      read: true,
    },
  ];

  // Fetch bookings data
  useEffect(() => {
    fetchBookings();
  }, []);

  // Add WebSocket connection
  useEffect(() => {
    // Connect to WebSocket
    websocketService.connect({
      id: "user-123", // Replace with actual user ID
      name: "Admin User", // Replace with actual user name
      role: "admin",
    });

    // Connection status handlers
    const unsubConnected = websocketService.on("connected", () => {
      setConnectionStatus("connected");
      window.toast?.success("Connected to real-time updates");

      // Request initial activity metrics after connection
      websocketService.emit("requestActivityMetrics");
    });

    const unsubDisconnected = websocketService.on("disconnected", () => {
      setConnectionStatus("disconnected");
      window.toast?.warning("Disconnected from real-time updates");
    });

    const unsubReconnecting = websocketService.on("reconnecting", (attempt) => {
      setConnectionStatus("reconnecting");
      window.toast?.info(`Reconnecting... (attempt ${attempt})`);
    });

    // Real-time change handlers
    const unsubBookingChange = websocketService.on(
      "bookingChange",
      (change) => {
        handleRealtimeChange(change);
      }
    );

    const unsubActiveUsers = websocketService.on("activeUsers", (users) => {
      console.log("Active users:", users);
      setActiveUsers(users);
    });

    const unsubActivityMetrics = websocketService.on(
      "activityMetrics",
      (metrics) => {
        setActivityMetrics(metrics);
      }
    );

    const unsubAlerts = websocketService.on("criticalAlerts", (alerts) => {
      setRealtimeAlerts((prev) => [...alerts, ...prev].slice(0, 50));

      // Show toast for each alert
      alerts.forEach((alert) => {
        window.toast?.error(alert.message, 10000);
      });
    });

    // Cleanup
    return () => {
      unsubConnected();
      unsubDisconnected();
      unsubReconnecting();
      unsubBookingChange();
      unsubActiveUsers();
      unsubActivityMetrics();
      unsubAlerts();
      websocketService.disconnect();
    };
  }, []);

  // Re-fetch when filters, sort, or search changes (reset to page 1)
  useEffect(() => {
    setCurrentPage(1);
    fetchBookings(false, 1);
  }, [filters, sortConfig, searchTerm]);

  // Clean up expired real-time highlights
  useEffect(() => {
    const interval = setInterval(() => {
      setRealtimeChanges((prev) => {
        const now = Date.now();
        const newChanges = new Map();

        prev.forEach((change, bookingId) => {
          if (change.expiresAt > now) {
            newChanges.set(bookingId, change);
          }
        });

        return newChanges;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Handle real-time changes
  const handleRealtimeChange = (change) => {
    console.log("Real-time change:", change);

    setRealtimeChanges((prev) => {
      const newChanges = new Map(prev);
      newChanges.set(change.bookingId, {
        ...change,
        expiresAt: Date.now() + 10000, // Highlight for 10 seconds
      });
      return newChanges;
    });

    // Update local bookings if the changed booking is in current view
    if (change.type === "update" || change.type === "insert") {
      setBookings((prevBookings) => {
        const index = prevBookings.findIndex((b) => b._id === change.bookingId);

        if (change.type === "insert" && index === -1) {
          // New booking - add to beginning
          window.toast?.success(`New booking created: ${change.bookingCode}`);
          return [change.data, ...prevBookings];
        } else if (change.type === "update" && index !== -1) {
          // Update existing booking
          const updatedBookings = [...prevBookings];
          updatedBookings[index] = {
            ...updatedBookings[index],
            ...change.currentValues,
          };
          return updatedBookings;
        }

        return prevBookings;
      });
    } else if (change.type === "delete") {
      setBookings((prevBookings) =>
        prevBookings.filter((b) => b._id !== change.bookingId)
      );
    }

    // Clean up expired changes
    setTimeout(() => {
      setRealtimeChanges((prev) => {
        const newChanges = new Map(prev);
        newChanges.delete(change.bookingId);
        return newChanges;
      });
    }, 10000);
  };

  const fetchBookings = async (refresh = false, page = null) => {
    try {
      setLoading(true);
      if (refresh) {
        setIsRefreshing(true);
      }

      // Use the current page or the page parameter
      const pageToFetch = page || currentPage;

      // Use the environment variable for API URL and the new booking routes
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/bookings`,
        {
          params: {
            refresh: refresh ? "true" : "false",
            sortBy: sortConfig.key,
            sortOrder: sortConfig.direction,
            limit: pageSize,
            page: pageToFetch,
            // Add any active filters
            ...(filters.status && { status: filters.status }),
            ...(filters.aduana && { aduana: filters.aduana }),
            ...(filters.dateRange.start && {
              dateStart: filters.dateRange.start,
            }),
            ...(filters.dateRange.end && { dateEnd: filters.dateRange.end }),
            ...(searchTerm && { search: searchTerm }),
          },
        }
      );

      // The API should now return { bookings, pagination } structure
      const { bookings: bookingsData, pagination } = response.data;

      setBookings(bookingsData);
      setFilteredBookings(bookingsData);

      // Update pagination state
      setCurrentPage(pagination.page);
      setTotalPages(pagination.pages);
      setTotalRecords(pagination.total);

      setLoading(false);
      setIsRefreshing(false);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const addStatus = async (bookingId, newStatus) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/bookings/${bookingId}/status`,
        {
          status: newStatus,
        }
      );

      // Refresh bookings to get the updated data
      await fetchBookings();

      // Show success message
      console.log(`Status "${newStatus.status}" added successfully`);
    } catch (error) {
      console.error("Error adding status:", error);
      alert(
        `Error adding status: ${error.response?.data?.message || error.message}`
      );
    }
  };

  // Get nested value helper
  const getNestedValue = (obj, path) => {
    if (!obj) return null;

    // Handle special case for latest status
    if (path === "latestStatus") {
      return obj.status && obj.status.length
        ? obj.status[obj.status.length - 1].status
        : "";
    }

    // Handle special case for latest ETA
    if (path === "latestETA") {
      return obj.fechaETA && obj.fechaETA.length
        ? new Date(obj.fechaETA[obj.fechaETA.length - 1].fechaETA)
        : null;
    }

    const keys = path.split(".");
    return keys.reduce((o, key) => (o || {})[key], obj) || "";
  };

  // Handle sorting
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Handle editing
  const startEditing = (id, booking) => {
    setEditingId(id);
    setEditData({
      fechaGlosa: booking.fechaGlosa,
      fechaAlta: booking.fechaAlta,
      fechaETA:
        booking.fechaETA && booking.fechaETA.length > 0
          ? booking.fechaETA[booking.fechaETA.length - 1].fechaETA
          : null,
      fechaPiso: booking.fechaPiso,
      fechaAtraco: booking.fechaAtraco,
      fechaCita:
        booking.fechaCita && booking.fechaCita.length > 0
          ? booking.fechaCita[booking.fechaCita.length - 1].fecha
          : null,
      fechaReprogramacion: booking.fechaReprogramacion,
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEditing = async (id) => {
    try {
      const bookingToUpdate = bookings.find((booking) => booking._id === id);
      if (!bookingToUpdate) return;

      // Prepare data for API
      const updateData = {
        ...editData,
        // Handle arrays for fechaETA and fechaCita
        fechaETA: editData.fechaETA
          ? [
              ...(bookingToUpdate.fechaETA || []),
              {
                fechaETA: editData.fechaETA,
                actualizadoPor: "current.user@wisdomfo.com", // Replace with actual user
                fechaActualizacion: new Date(),
              },
            ]
          : bookingToUpdate.fechaETA,

        fechaCita: editData.fechaCita
          ? [
              ...(bookingToUpdate.fechaCita || []),
              {
                fecha: editData.fechaCita,
                actualizadoPor: "current.user@wisdomfo.com", // Replace with actual user
                fechaActualizacion: new Date(),
              },
            ]
          : bookingToUpdate.fechaCita,
      };

      // Call API to update booking
      await axios.put(`/api/bookings/${id}`, updateData);

      // Update local state to reflect changes
      const updatedBookings = bookings.map((booking) => {
        if (booking._id === id) {
          return {
            ...booking,
            fechaGlosa: editData.fechaGlosa,
            fechaAlta: editData.fechaAlta,
            fechaPiso: editData.fechaPiso,
            fechaAtraco: editData.fechaAtraco,
            fechaReprogramacion: editData.fechaReprogramacion,
            fechaETA: updateData.fechaETA,
            fechaCita: updateData.fechaCita,
            updatedAt: new Date(),
          };
        }
        return booking;
      });

      setBookings(updatedBookings);
      setEditingId(null);
      setEditData({});
    } catch (error) {
      console.error("Error updating booking:", error);
      // Implement error handling UI feedback here
    }
  };

  // Handle change in edit data
  const handleEditChange = (field, value) => {
    setEditData({
      ...editData,
      [field]: value,
    });
  };

  // Handle keyboard shortcut actions
  const handleShortcutAction = useCallback(
    (action) => {
      switch (action) {
        case "edit":
          if (selectedBooking) {
            startEditing(selectedBooking._id, selectedBooking);
          }
          break;
        case "save":
          if (editingId) {
            saveEditing(editingId);
          }
          break;
        case "cancel":
          if (editingId) {
            cancelEditing();
          }
          break;
        case "focusSearch":
          document
            .querySelector('input[placeholder*="Search bookings"]')
            ?.focus();
          break;
        case "refresh":
          fetchBookings(true);
          break;
        case "export":
          window.location.href = `${
            process.env.REACT_APP_BACKEND_API_URL
          }/api/bookings/export/excel${getExportQueryParams()}`;
          break;
        case "escape":
          // Close any open modal or dropdown
          if (showHistoryModal) {
            setShowHistoryModal(false);
          } else if (showNotifications) {
            setShowNotifications(false);
          } else if (showUserMenu) {
            setShowUserMenu(false);
          } else if (showFilters) {
            setShowFilters(false);
          } else if (editingId) {
            cancelEditing();
          }
          break;
        default:
          break;
      }
    },
    [
      selectedBooking,
      editingId,
      showHistoryModal,
      showNotifications,
      showUserMenu,
      showFilters,
    ]
  );

  // Get unique status values for filter dropdown
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set();
    bookings.forEach((booking) => {
      if (booking.status && booking.status.length > 0) {
        statuses.add(booking.status[booking.status.length - 1].status);
      }
    });
    return Array.from(statuses);
  }, [bookings]);

  // Get unique aduana values for filter dropdown
  const uniqueAduanas = useMemo(() => {
    const aduanas = new Set();
    bookings.forEach((booking) => {
      if (booking.aduana) {
        aduanas.add(booking.aduana);
      }
    });
    return Array.from(aduanas);
  }, [bookings]);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      status: "",
      aduana: "",
      dateRange: {
        start: null,
        end: null,
      },
    });
    setSearchTerm("");
  };

  // View booking history
  const viewHistory = (booking) => {
    setSelectedBooking(booking);
    setShowHistoryModal(true);
  };

  // Handle single field update
  const updateSingleField = async (bookingId, field, value) => {
    try {
      // Get field display name for toast messages
      const fieldDisplayNames = {
        fechaGlosa: "Glosa Date",
        fechaAlta: "Alta Date",
        fechaETA: "ETA Date",
        fechaPiso: "Piso Date",
        fechaAtraco: "Atraco Date",
        fechaReprogramacion: "Reprogramacion Date",
      };

      // Special handling for ETA field which uses an array structure
      if (field === "fechaETA") {
        const booking = bookings.find((b) => b._id === bookingId);
        if (!booking) return;

        const updatedETA = [
          ...(booking.fechaETA || []),
          {
            fechaETA: value,
            actualizadoPor: "current.user@wisdomfo.com", // Replace with actual user
            fechaActualizacion: new Date(),
          },
        ];

        await axios.patch(
          `${process.env.REACT_APP_BACKEND_API_URL}/api/bookings/${bookingId}/field/${field}`,
          {
            value: updatedETA,
          }
        );

        // Update local state
        setBookings(
          bookings.map((b) =>
            b._id === bookingId ? { ...b, fechaETA: updatedETA } : b
          )
        );

        // Show success message with toast
        if (window.toast) {
          window.toast.success(
            `${fieldDisplayNames[field]} updated successfully`
          );
        }
      } else {
        // Regular field update
        await axios.patch(
          `${process.env.REACT_APP_BACKEND_API_URL}/api/bookings/${bookingId}/field/${field}`,
          {
            value,
          }
        );

        // Update local state
        setBookings(
          bookings.map((b) =>
            b._id === bookingId ? { ...b, [field]: value } : b
          )
        );

        // Show success message with toast
        if (window.toast) {
          window.toast.success(
            `${fieldDisplayNames[field]} updated successfully`
          );
        }
      }
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      // Show error message with toast
      if (window.toast) {
        window.toast.error(`Error updating field: ${error.message}`);
      } else {
        alert(`Error updating field: ${error.message}`);
      }
    }
  };

  // Handle a row being selected
  const handleSelectRow = (booking) => {
    setSelectedBooking(booking);
  };

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      {/* Toast container for notifications */}
      <ToastContainer />

      <div className="w-full bg-white shadow-lg overflow-hidden flex-1">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#003366] to-blue-700 text-white">
          {/* Corporate Menu Bar */}
          <div className="flex justify-end items-center px-6 py-2">
            <div className="flex items-center space-x-4 text-sm">
              <button className="text-blue-100 hover:text-white flex items-center">
                <FontAwesomeIcon icon={faQuestionCircle} className="mr-1" />
                <span>Help</span>
              </button>

              {/* Notifications dropdown */}
              <div className="relative dropdown-container">
                <button
                  className="text-blue-100 hover:text-white flex items-center"
                  onClick={() => {
                    setShowNotifications(!showNotifications);
                    setShowUserMenu(false);
                  }}
                >
                  <div className="relative">
                    <FontAwesomeIcon icon={faBell} />
                    {sampleNotifications.some((n) => !n.read) && (
                      <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full"></span>
                    )}
                  </div>
                  <span className="ml-1">Notifications</span>
                </button>

                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg overflow-hidden z-20 text-gray-800 dropdown-container">
                    <div className="py-2 px-3 bg-gray-100 border-b border-gray-200 flex justify-between items-center">
                      <span className="font-medium">Notifications</span>
                      <span className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
                        Mark all as read
                      </span>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {sampleNotifications.length > 0 ? (
                        sampleNotifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={`px-4 py-3 border-b border-gray-100 hover:bg-gray-50 ${
                              !notification.read ? "bg-blue-50" : ""
                            }`}
                          >
                            <p className="text-sm mb-1">{notification.text}</p>
                            <p className="text-xs text-gray-500">
                              {notification.time}
                            </p>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-6 text-center text-gray-500">
                          No new notifications
                        </div>
                      )}
                    </div>
                    <div className="py-2 px-3 bg-gray-100 border-t border-gray-200 text-center">
                      <a
                        href="#"
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        View all notifications
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* User menu dropdown */}
              <div className="relative ml-2 dropdown-container">
                <button
                  className="flex items-center space-x-2 bg-blue-800 hover:bg-blue-900 px-3 py-1 rounded text-sm transition-colors duration-200"
                  onClick={() => {
                    setShowUserMenu(!showUserMenu);
                    setShowNotifications(false);
                  }}
                >
                  <div className="h-6 w-6 rounded-full bg-blue-200 flex items-center justify-center text-blue-800">
                    <FontAwesomeIcon icon={faUser} size="xs" />
                  </div>
                  <span>Admin User</span>
                  <FontAwesomeIcon icon={faChevronDown} size="xs" />
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg overflow-hidden z-20 text-gray-800 dropdown-container">
                    <div className="py-3 px-4 border-b border-gray-100">
                      <p className="text-sm font-medium">Admin User</p>
                      <p className="text-xs text-gray-500">
                        admin@wisdomfo.com
                      </p>
                    </div>
                    <div>
                      <a
                        href="#"
                        className="block px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        <FontAwesomeIcon
                          icon={faUser}
                          className="mr-2 text-gray-500"
                        />
                        My Profile
                      </a>
                      <a
                        href="#"
                        className="block px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        <FontAwesomeIcon
                          icon={faCog}
                          className="mr-2 text-gray-500"
                        />
                        Account Settings
                      </a>
                      <a
                        href="#"
                        className="block px-4 py-2 text-sm border-t border-gray-100 hover:bg-gray-100 text-red-600"
                      >
                        <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
                        Sign Out
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main header with title and buttons */}
          <div className="flex justify-between items-center px-6 pt-5 pb-7">
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon icon={faShip} className="text-2xl" />
              <h1 className="text-2xl font-bold tracking-tight">
                Booking Control Deck
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => fetchBookings(true, currentPage)}
                className={`flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isRefreshing ? "opacity-75" : ""
                }`}
                disabled={isRefreshing}
              >
                <FontAwesomeIcon
                  icon={faSync}
                  className={isRefreshing ? "animate-spin" : ""}
                />
                <span>{isRefreshing ? "Refreshing..." : "Refresh Data"}</span>
              </button>
              <button
                onClick={() => setShowRealTimeDashboard(true)}
                className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 relative"
              >
                <FontAwesomeIcon icon={faBolt} />
                <span>Real-Time Monitor</span>

                {/* Connection indicator */}
                <div
                  className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${
                    connectionStatus === "connected"
                      ? "bg-green-400"
                      : connectionStatus === "reconnecting"
                      ? "bg-yellow-400 animate-pulse"
                      : "bg-red-400"
                  }`}
                />

                {/* Activity indicator */}
                {activityMetrics?.recentChanges?.length > 0 && (
                  <motion.div
                    className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring" }}
                  >
                    {activityMetrics.recentChanges.length}
                  </motion.div>
                )}
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center space-x-2 ${
                  showFilters ? "bg-blue-800" : "bg-blue-600 hover:bg-blue-700"
                } px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200`}
              >
                <FontAwesomeIcon icon={faFilter} />
                <span>{showFilters ? "Hide Filters" : "Show Filters"}</span>
              </button>
              <button
                onClick={() =>
                  (window.location.href = `${
                    process.env.REACT_APP_BACKEND_API_URL
                  }/api/bookings/export/excel${getExportQueryParams()}`)
                }
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faDownload} />
                <span>Export</span>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex px-6 border-b border-blue-600">
            <button className="px-4 py-2 font-medium text-sm rounded-t-lg bg-white text-blue-800">
              <FontAwesomeIcon icon={faTable} className="mr-2" />
              Bookings Grid
            </button>
            <button className="px-4 py-2 font-medium text-sm rounded-t-lg text-blue-100 hover:bg-blue-800">
              <FontAwesomeIcon icon={faHistory} className="mr-2" />
              Timeline View
            </button>
            <button className="px-4 py-2 font-medium text-sm rounded-t-lg text-blue-100 hover:bg-blue-800">
              <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
              Reports
            </button>
          </div>
        </div>

        {/* Content area */}
        <div className="p-6 overflow-y-auto">
          {/* Search and filters */}
          <div className="mb-6">
            <div className="flex mb-4">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    // Note: The useEffect will handle the page reset and fetching
                  }}
                  placeholder="Search bookings by code, reference, client name or B/L number..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              {showFilters && (
                <button
                  onClick={resetFilters}
                  className="ml-2 px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md text-sm font-medium"
                >
                  Clear Filters
                </button>
              )}
            </div>

            {/* Advanced filters */}
            {showFilters && (
              <div className="bg-gray-100 p-4 rounded-lg mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) =>
                      handleFilterChange("status", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Statuses</option>
                    {uniqueStatuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Aduana
                  </label>
                  <select
                    value={filters.aduana}
                    onChange={(e) =>
                      handleFilterChange("aduana", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Aduanas</option>
                    {uniqueAduanas.map((aduana) => (
                      <option key={aduana} value={aduana}>
                        {aduana}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date Range
                  </label>
                  <div className="flex space-x-2">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FontAwesomeIcon
                          icon={faCalendarAlt}
                          className="text-gray-400"
                        />
                      </div>
                      <DatePicker
                        selected={filters.dateRange.start}
                        onChange={(date) =>
                          handleFilterChange("dateRange", {
                            ...filters.dateRange,
                            start: date,
                          })
                        }
                        className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholderText="Start date"
                        dateFormat="MM/dd/yyyy"
                      />
                    </div>
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FontAwesomeIcon
                          icon={faCalendarAlt}
                          className="text-gray-400"
                        />
                      </div>
                      <DatePicker
                        selected={filters.dateRange.end}
                        onChange={(date) =>
                          handleFilterChange("dateRange", {
                            ...filters.dateRange,
                            end: date,
                          })
                        }
                        className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholderText="End date"
                        dateFormat="MM/dd/yyyy"
                        minDate={filters.dateRange.start}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Results summary */}
            <div className="flex justify-between items-center text-sm text-gray-600">
              <div>
                Showing {bookings.length} bookings on this page ({totalRecords}{" "}
                total)
              </div>
              <div>Last updated: {new Date().toLocaleString("es-MX")}</div>
            </div>
          </div>

          {/* Data grid */}
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <FontAwesomeIcon
                icon={faSync}
                spin
                className="text-blue-600 text-2xl mr-2"
              />
              <span className="text-gray-600 font-medium">
                Loading bookings...
              </span>
            </div>
          ) : (
            <div className="overflow-x-auto bg-white rounded-lg shadow">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-14">
                      Actions
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("bookingCode")}
                    >
                      <div className="flex items-center">
                        <span>Booking Code</span>
                        {sortConfig.key === "bookingCode" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faSortUp
                                : faSortDown
                            }
                            className="ml-1"
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("referencia")}
                    >
                      <div className="flex items-center">
                        <span>Reference</span>
                        {sortConfig.key === "referencia" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faSortUp
                                : faSortDown
                            }
                            className="ml-1"
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("cliente.razonSocial")}
                    >
                      <div className="flex items-center">
                        <span>Client</span>
                        {sortConfig.key === "cliente.razonSocial" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faSortUp
                                : faSortDown
                            }
                            className="ml-1"
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("latestStatus")}
                    >
                      <div className="flex items-center">
                        <span>Status</span>
                        {sortConfig.key === "latestStatus" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faSortUp
                                : faSortDown
                            }
                            className="ml-1"
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("bolMaster.0.numeroBolMaster")}
                    >
                      <div className="flex items-center">
                        <span>B/L Number</span>
                        {sortConfig.key === "bolMaster.0.numeroBolMaster" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faSortUp
                                : faSortDown
                            }
                            className="ml-1"
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("fechaGlosa")}
                    >
                      <div className="flex items-center">
                        <span>Glosa Date</span>
                        {sortConfig.key === "fechaGlosa" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faSortUp
                                : faSortDown
                            }
                            className="ml-1"
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("fechaAlta")}
                    >
                      <div className="flex items-center">
                        <span>Alta Date</span>
                        {sortConfig.key === "fechaAlta" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faSortUp
                                : faSortDown
                            }
                            className="ml-1"
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("latestETA")}
                    >
                      <div className="flex items-center">
                        <span>ETA Date</span>
                        {sortConfig.key === "latestETA" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faSortUp
                                : faSortDown
                            }
                            className="ml-1"
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("fechaPiso")}
                    >
                      <div className="flex items-center">
                        <span>Piso Date</span>
                        {sortConfig.key === "fechaPiso" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faSortUp
                                : faSortDown
                            }
                            className="ml-1"
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("fechaAtraco")}
                    >
                      <div className="flex items-center">
                        <span>Atraco Date</span>
                        {sortConfig.key === "fechaAtraco" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faSortUp
                                : faSortDown
                            }
                            className="ml-1"
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("fechaReprogramacion")}
                    >
                      <div className="flex items-center">
                        <span>Reprog. Date</span>
                        {sortConfig.key === "fechaReprogramacion" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faSortUp
                                : faSortDown
                            }
                            className="ml-1"
                          />
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {KPI_RULES.prealertaKpi.name}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {KPI_RULES.altaPisoKpi.name}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {KPI_RULES.altaAtracoKpi.name}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {KPI_RULES.altaGlosaKpi.name}
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("aduana")}
                    >
                      <div className="flex items-center">
                        <span>Aduana</span>
                        {sortConfig.key === "aduana" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faSortUp
                                : faSortDown
                            }
                            className="ml-1"
                          />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBookings.length === 0 ? (
                    <tr>
                      <td
                        colSpan="13"
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No bookings match your search criteria
                      </td>
                    </tr>
                  ) : (
                    filteredBookings.map((booking, index) => {
                      const isEditing = editingId === booking._id;
                      const latestStatus =
                        booking.status && booking.status.length > 0
                          ? booking.status[booking.status.length - 1].status
                          : null;
                      const latestETA =
                        booking.fechaETA && booking.fechaETA.length > 0
                          ? booking.fechaETA[booking.fechaETA.length - 1]
                              .fechaETA
                          : null;

                      const realtimeChange = realtimeChanges.get(booking._id);
                      const hasRealtimeChange = !!realtimeChange;

                      return (
                        <motion.tr
                          key={booking._id}
                          className={`${
                            index % 2 === 0
                              ? "bg-white"
                              : "bg-gray-50 hover:bg-blue-50"
                          } ${
                            selectedBooking?._id === booking._id
                              ? "bg-blue-50"
                              : ""
                          } ${
                            hasRealtimeChange
                              ? "ring-2 ring-purple-400 ring-opacity-50"
                              : ""
                          } relative transition-all duration-300`}
                          onClick={() => handleSelectRow(booking)}
                          animate={
                            hasRealtimeChange
                              ? {
                                  backgroundColor: [
                                    "rgba(147, 51, 234, 0.1)",
                                    "rgba(147, 51, 234, 0)",
                                    "rgba(147, 51, 234, 0.1)",
                                  ],
                                }
                              : {}
                          }
                          transition={{
                            duration: 2,
                            repeat: hasRealtimeChange ? Infinity : 0,
                          }}
                        >
                          {/* Add change indicator */}
                          {hasRealtimeChange && (
                            <td className="absolute left-0 top-0 bottom-0 w-1 bg-purple-500">
                              <motion.div
                                className="h-full w-full bg-purple-400"
                                animate={{ opacity: [1, 0.3, 1] }}
                                transition={{ duration: 1, repeat: Infinity }}
                              />
                            </td>
                          )}
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex space-x-1">
                              {isEditing ? (
                                <>
                                  <button
                                    onClick={() => saveEditing(booking._id)}
                                    className="text-green-600 hover:text-green-900"
                                    title="Save changes"
                                  >
                                    <FontAwesomeIcon icon={faSave} />
                                  </button>
                                  <button
                                    onClick={cancelEditing}
                                    className="text-red-600 hover:text-red-900"
                                    title="Cancel"
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() =>
                                      startEditing(booking._id, booking)
                                    }
                                    className="text-blue-600 hover:text-blue-900"
                                    title="Edit booking"
                                  >
                                    <FontAwesomeIcon icon={faEdit} />
                                  </button>
                                  <button
                                    onClick={() => viewHistory(booking)}
                                    className="text-indigo-600 hover:text-indigo-900"
                                    title="View history"
                                  >
                                    <FontAwesomeIcon icon={faHistory} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {booking.bookingCode}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {booking.referencia}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {booking.cliente?.razonSocial || "-"}
                          </td>
                          <td
                            className={`px-4 py-4 whitespace-nowrap text-sm text-gray-900 ${
                              realtimeChange?.changedFields?.includes("status")
                                ? "bg-yellow-100 transition-colors duration-1000"
                                : ""
                            }`}
                          >
                            <div className="flex items-center">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  statusColors[latestStatus] ||
                                  statusColors.default
                                }`}
                              >
                                {latestStatus || "N/A"}
                              </span>
                              {booking.status && booking.status.length > 1 && (
                                <span className="ml-2 text-xs text-gray-500">
                                  +{booking.status.length - 1}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {(booking.bolMaster &&
                              booking.bolMaster[0]?.numeroBolMaster) ||
                              "-"}
                          </td>
                          <td
                            className={`whitespace-nowrap text-sm text-gray-500 ${
                              realtimeChange?.changedFields?.includes(
                                "fechaGlosa"
                              )
                                ? "bg-yellow-100 transition-colors duration-1000"
                                : ""
                            }`}
                          >
                            <DataCell
                              type="date"
                              value={
                                isEditing
                                  ? editData.fechaGlosa
                                  : booking.fechaGlosa
                              }
                              onChange={(value) =>
                                handleEditChange("fechaGlosa", value)
                              }
                              onSave={(id, value) => {
                                const updatedEditData = {
                                  ...editData,
                                  fechaGlosa: value,
                                };
                                setEditData(updatedEditData);

                                if (editingId !== booking._id) {
                                  updateSingleField(
                                    booking._id,
                                    "fechaGlosa",
                                    value
                                  );
                                }
                              }}
                              fieldName="Glosa Date"
                              id={`${booking._id}-fechaGlosa`}
                              parentEditingId={editingId}
                              setParentEditingId={setEditingId}
                              readOnlyFormatter={formatDate}
                              disabled={false} // Allow editing even when row isn't in edit mode
                            />
                          </td>
                          <td
                            className={`whitespace-nowrap text-sm text-gray-500 ${
                              realtimeChange?.changedFields?.includes(
                                "fechaAlta"
                              )
                                ? "bg-yellow-100 transition-colors duration-1000"
                                : ""
                            }`}
                          >
                            <DataCell
                              type="date"
                              value={
                                isEditing
                                  ? editData.fechaAlta
                                  : booking.fechaAlta
                              }
                              onChange={(value) =>
                                handleEditChange("fechaAlta", value)
                              }
                              onSave={(id, value) => {
                                const updatedEditData = {
                                  ...editData,
                                  fechaAlta: value,
                                };
                                setEditData(updatedEditData);

                                if (editingId !== booking._id) {
                                  updateSingleField(
                                    booking._id,
                                    "fechaAlta",
                                    value
                                  );
                                }
                              }}
                              fieldName="Alta Date"
                              id={`${booking._id}-fechaAlta`}
                              parentEditingId={editingId}
                              setParentEditingId={setEditingId}
                              readOnlyFormatter={formatDate}
                              disabled={false}
                            />
                          </td>
                          <td
                            className={`whitespace-nowrap text-sm text-gray-500 ${
                              realtimeChange?.changedFields?.includes(
                                "fechaETA"
                              )
                                ? "bg-yellow-100 transition-colors duration-1000"
                                : ""
                            }`}
                          >
                            <DataCell
                              type="date"
                              value={isEditing ? editData.fechaETA : latestETA}
                              onChange={(value) =>
                                handleEditChange("fechaETA", value)
                              }
                              onSave={(id, value) => {
                                const updatedEditData = {
                                  ...editData,
                                  fechaETA: value,
                                };
                                setEditData(updatedEditData);

                                if (editingId !== booking._id) {
                                  updateSingleField(
                                    booking._id,
                                    "fechaETA",
                                    value
                                  );
                                }
                              }}
                              fieldName="ETA Date"
                              id={`${booking._id}-fechaETA`}
                              parentEditingId={editingId}
                              setParentEditingId={setEditingId}
                              readOnlyFormatter={formatDate}
                              disabled={false}
                            />
                          </td>
                          <td
                            className={`whitespace-nowrap text-sm text-gray-500 ${
                              realtimeChange?.changedFields?.includes(
                                "fechaPiso"
                              )
                                ? "bg-yellow-100 transition-colors duration-1000"
                                : ""
                            }`}
                          >
                            <DataCell
                              type="date"
                              value={
                                isEditing
                                  ? editData.fechaPiso
                                  : booking.fechaPiso
                              }
                              onChange={(value) =>
                                handleEditChange("fechaPiso", value)
                              }
                              onSave={(id, value) => {
                                const updatedEditData = {
                                  ...editData,
                                  fechaPiso: value,
                                };
                                setEditData(updatedEditData);

                                if (editingId !== booking._id) {
                                  updateSingleField(
                                    booking._id,
                                    "fechaPiso",
                                    value
                                  );
                                }
                              }}
                              fieldName="Piso Date"
                              id={`${booking._id}-fechaPiso`}
                              parentEditingId={editingId}
                              setParentEditingId={setEditingId}
                              readOnlyFormatter={formatDate}
                              disabled={false}
                            />
                          </td>
                          <td
                            className={`whitespace-nowrap text-sm text-gray-500 ${
                              realtimeChange?.changedFields?.includes(
                                "fechaAtraco"
                              )
                                ? "bg-yellow-100 transition-colors duration-1000"
                                : ""
                            }`}
                          >
                            <DataCell
                              type="date"
                              value={
                                isEditing
                                  ? editData.fechaAtraco
                                  : booking.fechaAtraco
                              }
                              onChange={(value) =>
                                handleEditChange("fechaAtraco", value)
                              }
                              onSave={(id, value) => {
                                const updatedEditData = {
                                  ...editData,
                                  fechaAtraco: value,
                                };
                                setEditData(updatedEditData);

                                if (editingId !== booking._id) {
                                  updateSingleField(
                                    booking._id,
                                    "fechaAtraco",
                                    value
                                  );
                                }
                              }}
                              fieldName="Atraco Date"
                              id={`${booking._id}-fechaAtraco`}
                              parentEditingId={editingId}
                              setParentEditingId={setEditingId}
                              readOnlyFormatter={formatDate}
                              disabled={false}
                            />
                          </td>
                          <td
                            className={`whitespace-nowrap text-sm text-gray-500 ${
                              realtimeChange?.changedFields?.includes(
                                "fechaReprogramacion"
                              )
                                ? "bg-yellow-100 transition-colors duration-1000"
                                : ""
                            }`}
                          >
                            <DataCell
                              type="date"
                              value={
                                isEditing
                                  ? editData.fechaReprogramacion
                                  : booking.fechaReprogramacion
                              }
                              onChange={(value) =>
                                handleEditChange("fechaReprogramacion", value)
                              }
                              onSave={(id, value) => {
                                const updatedEditData = {
                                  ...editData,
                                  fechaReprogramacion: value,
                                };
                                setEditData(updatedEditData);

                                if (editingId !== booking._id) {
                                  updateSingleField(
                                    booking._id,
                                    "fechaReprogramacion",
                                    value
                                  );
                                }
                              }}
                              fieldName="Reprogramacion Date"
                              id={`${booking._id}-fechaReprogramacion`}
                              parentEditingId={editingId}
                              setParentEditingId={setEditingId}
                              readOnlyFormatter={formatDate}
                              disabled={false}
                            />
                          </td>
                          <td>
                            <KpiCell
                              {...calculatePrealertaKpi(booking)}
                              tooltip={KPI_RULES.prealertaKpi.description}
                            />
                          </td>
                          <td>
                            <KpiCell
                              {...calculateAltaPisoKpi(booking)}
                              tooltip={KPI_RULES.altaPisoKpi.description}
                            />
                          </td>
                          <td>
                            <KpiCell
                              {...calculateAltaAtracoKpi(booking)}
                              tooltip={KPI_RULES.altaAtracoKpi.description}
                            />
                          </td>
                          <td>
                            <KpiCell
                              {...calculateAltaGlosaKpi(booking)}
                              tooltip={KPI_RULES.altaGlosaKpi.description}
                            />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {booking.aduana || "-"}
                          </td>
                        </motion.tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-100 px-6 py-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Showing {(currentPage - 1) * pageSize + 1} -{" "}
              {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}{" "}
              bookings
            </p>
            {!loading && totalPages > 1 && (
              <div className="flex items-center text-sm">
                <span className="text-gray-700 mr-2">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => {
                    const newPage = currentPage - 1;
                    setCurrentPage(newPage);
                    fetchBookings(false, newPage);
                  }}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 border border-gray-300 rounded-md text-sm font-medium mr-1 ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50 cursor-pointer"
                  }`}
                >
                  Previous
                </button>

                {/* Optional: Add page number buttons */}
                <div className="flex mx-2">
                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (currentPage <= 3) {
                      pageNum = idx + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = currentPage - 2 + idx;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => {
                          setCurrentPage(pageNum);
                          fetchBookings(false, pageNum);
                        }}
                        className={`px-3 py-1 mx-0.5 border rounded-md text-sm font-medium ${
                          currentPage === pageNum
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => {
                    const newPage = currentPage + 1;
                    setCurrentPage(newPage);
                    fetchBookings(false, newPage);
                  }}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 border border-gray-300 rounded-md text-sm font-medium ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50 cursor-pointer"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status History Modal */}
      {showHistoryModal && selectedBooking && (
        <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl">
            <div className="bg-gradient-to-r from-[#003366] to-blue-700 text-white px-6 py-4 rounded-t-lg flex justify-between items-center">
              <h3 className="text-lg font-medium">
                Status History for {selectedBooking.bookingCode}
              </h3>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-white hover:text-blue-200"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="px-6 py-4 max-h-96 overflow-y-auto">
              <StatusHistory
                booking={selectedBooking}
                onAddStatus={addStatus}
              />
            </div>
            <div className="bg-gray-100 px-6 py-3 rounded-b-lg">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Real-Time Dashboard Modal */}
      {showRealTimeDashboard && (
        <RealTimeDashboard
          onClose={() => setShowRealTimeDashboard(false)}
          metrics={activityMetrics}
          alerts={realtimeAlerts}
          activeUsers={activeUsers}
        />
      )}

      {/* Keyboard Shortcuts Helper */}
      <KeyboardShortcutsHelper
        isOpen={showKeyboardShortcuts}
        onToggle={setShowKeyboardShortcuts}
        onShortcutAction={handleShortcutAction}
      />
    </div>
  );
};

export default BookingControlDeck;
